// Central place for internal-user role → module access rules.
// Keep every nav/route/home-card gate keyed off these helpers so access stays
// consistent across the app (the backend remains the real security boundary).

export const EMS_PATH = '/ems';
export const CAMPAIGNS_PATH = '/campaigns';
export const DISPUTE_CONSOLE_PATH = '/dispute-console';

// Roles whose access is an explicit allow-list rather than "everything".
// EXECUTIVE staff work the EMS queue, run campaign sends, and share the
// Dispute Console with the other operational roles — and see nothing else.
// Anything not listed here is unrestricted.
const RESTRICTED_ROLE_PATHS = {
  EXECUTIVE: [EMS_PATH, CAMPAIGNS_PATH, DISPUTE_CONSOLE_PATH],
};

// True when the role only gets a fixed subset of modules.
export function isRestrictedRole(role) {
  return Object.prototype.hasOwnProperty.call(RESTRICTED_ROLE_PATHS, role);
}

// The paths a restricted role may reach, or null when the role is unrestricted.
export function allowedPathsForRole(role) {
  return RESTRICTED_ROLE_PATHS[role] ?? null;
}

// Where a restricted role lands when it hits a path it cannot see.
export function landingPathForRole(role) {
  return allowedPathsForRole(role)?.[0] ?? '/';
}

export function canAccessPath(role, path) {
  const allowed = allowedPathsForRole(role);
  return allowed === null || allowed.includes(path);
}

// Retained for callers that only ask the old question. EXECUTIVE is no longer
// EMS-only — it now also has Campaigns and the Dispute Console — so this
// reports false for it and callers should move to canAccessPath.
export function isEmsOnlyRole(role) {
  const allowed = allowedPathsForRole(role);
  return allowed !== null && allowed.length === 1 && allowed[0] === EMS_PATH;
}

// Roles allowed into the Dispute Console — mirrors the backend's
// DISPUTE_OPS_ACTION policy (auth/internalPermissions.ts), which every
// DisputeOps* endpoint is gated on. Kept in sync manually; the backend
// remains the real security boundary if this ever drifts.
export function isDisputeOpsRole(role) {
  return ['ADMIN', 'KAM', 'AE', 'ACCOUNT_EXECUTIVE', 'EXECUTIVE'].includes(role);
}

// Roles that may change who owns a client's GST uploads in the EMS GST Upload
// Tracker tab — mirrors the backend's GST_UPLOAD_OWNER_ASSIGN policy
// (auth/internalPermissions.ts). EXECUTIVE can view but not reassign.
export function canAssignGstOwners(role) {
  return role === 'ADMIN' || role === 'KAM';
}
